/**
 * Client for the M5 PRODUCTION control-plane endpoints (`/api/*`).
 *
 * Same shape as the dev client (`dev-api.ts`) — mint host/presentation tickets,
 * join by name — but against the deployed co-located control plane (DESIGN §5.1,
 * served same-origin behind CloudFront, M5.4). The route layout matches DESIGN
 * §5.1 so the M7 swap to signed tickets + real identity is additive.
 *
 * M5 targets the single demo game the server boots on startup, discovered via
 * `GET /api/game`; here every session resolves to that demo game so the deployed URL
 * is immediately playable. **Pack-aware game creation lands in M8** (`pack-api.ts`):
 * a host uploads/picks a pack and creates a dedicated game, minting a host ticket for
 * it — this `requestSpectatorTicket`/`joinAsPlayer` pair stays the ticket/join path
 * once a game exists.
 */

import type { Role } from '@jeopardy/shared';
import { SERVER_HTTP_URL } from '../config';
import { ApiError } from './api-error';
import type { HostTicket, PlayerTicket } from './api-types';
import { getSessionId } from './session-id';

/**
 * The header carrying the stable browser-session id (#35 session work) on control-plane
 * requests, so the server stamps `session.id` on the HTTP SERVER span — joining a user's
 * HTTP activity to their RUM session (and their WS gameplay, which carries the same id via
 * `?sid=`). RUM already stitches these HTTP spans to the browser by W3C `traceparent`; the
 * session id adds the durable same-session join across a user's many requests/games.
 */
export const SESSION_ID_HEADER = 'x-jeopardy-session-id';

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${SERVER_HTTP_URL}${path}`, {
    headers: { [SESSION_ID_HEADER]: getSessionId() },
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return (await res.json()) as T;
}

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(`${SERVER_HTTP_URL}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', [SESSION_ID_HEADER]: getSessionId() },
    body: JSON.stringify(body),
  });
}

/** Resolve the demo game's id (the default target when no join code is given). */
async function currentGameId(): Promise<string> {
  const { gameId } = await getJson<{ gameId: string }>('/api/game');
  return gameId;
}

/**
 * Resolve a host-shared join code → the game's id (M8 join-by-code). Lets a player
 * join the specific game a host just created, not the demo game. Throws a friendly
 * "No game found for that code." when the code doesn't match a live game.
 *
 * ROBUSTNESS: M10.3 scoped the CloudFront SPA rewrite OFF `/api/*` (a CloudFront
 * Function on the S3 default behavior only), so an unknown code now returns a real
 * `404 {error}` and this reads it directly. We KEEP the defensive "not valid JSON
 * with a gameId → no game found" fallback anyway — it costs nothing and still gives
 * the user the right message if a cache/config regression ever re-masks an `/api`
 * error as HTML, instead of a raw `JSON.parse` crash.
 */
export async function resolveGameByCode(code: string): Promise<string> {
  const res = await fetch(
    `${SERVER_HTTP_URL}/api/games/by-code/${encodeURIComponent(code.trim())}`,
    { headers: { [SESSION_ID_HEADER]: getSessionId() } },
  );
  // A 404 here is RETRYABLE (#67): on a non-owner task the CODE item may not have
  // propagated yet (read-your-write window). The join-chain retry re-reads within a
  // bounded budget; a 404 that persists past it surfaces as this definitive message.
  if (res.status === 404) throw new ApiError('No game found for that code.', 404);
  if (!res.ok)
    throw new ApiError(`Could not look up that code (${res.status})`, res.status);
  const body = (await res.json().catch(() => undefined)) as
    { gameId?: string } | undefined;
  // A 200 with no gameId is a config regression (an /api response masked as HTML), not
  // an eventual-consistency blip — definitive (no status → not retried).
  if (!body?.gameId) throw new ApiError('No game found for that code.');
  return body.gameId;
}

/**
 * Mint a host or presentation ticket for a game. `gameId` targets a SPECIFIC game —
 * the presentation screen passes the id it resolved from the host's join code
 * ({@link resolveGameByCode}, M8) so it binds to THAT game, not the demo game (#51).
 * When omitted, fall back to the demo game via `GET /api/game` (the host path, which
 * already resolved its own gameId at create time, never relies on this default for
 * presentation — see #51).
 */
export async function requestSpectatorTicket(
  role: Extract<Role, 'host' | 'presentation'>,
  gameId?: string,
): Promise<HostTicket> {
  const targetGameId = gameId ?? (await currentGameId());
  const res = await postJson(`/api/games/${encodeURIComponent(targetGameId)}/ticket`, {
    role,
  });
  if (!res.ok) throw new Error(`Failed to get ${role} ticket (${res.status})`);
  return (await res.json()) as HostTicket;
}

/**
 * Seat a player by name + mint their ticket. In prod, identity is proven by the
 * signed **guest token** (M7, FR-9.3) — the server resolves the seat from the
 * token, never a raw client id — so a refresh/reconnect keeps the same seat
 * (FR-2.6). Throws with the server's message on a rejected join (duplicate name,
 * game full, an invalid identity token, or a wrong/missing join code).
 * `playerId` is a dev/legacy fallback.
 *
 * `joinCode` is the REQUIRED entry check (M11.5.2, DESIGN §11): the server refuses a
 * join whose code doesn't match the target game's, so knowing the `gameId` alone is
 * not enough to get in. `gameId` targets a SPECIFIC game (resolved from that same code
 * via {@link resolveGameByCode} — M8); omitted → the demo game, whose code the client
 * discovers via `GET /api/game`, so opening `/player` with no code still lands
 * somewhere playable.
 */
export async function joinAsPlayer(
  name: string,
  identity?: { guestToken?: string; playerId?: string },
  gameId?: string,
  joinCode?: string,
): Promise<PlayerTicket> {
  // Resolve the target game + its join code together. With a code the caller already
  // resolved it to a gameId; without one, fall back to the demo game and read its
  // advertised code so the code check still passes (the demo URL stays playable).
  let targetGameId = gameId;
  let code = joinCode;
  if (!targetGameId || !code) {
    const demo = await getJson<{ gameId: string; joinCode: string }>('/api/game');
    targetGameId = targetGameId ?? demo.gameId;
    code = code ?? demo.joinCode;
  }
  const res = await postJson(`/api/games/${encodeURIComponent(targetGameId)}/join`, {
    name,
    joinCode: code,
    ...(identity?.guestToken ? { guestToken: identity.guestToken } : {}),
    ...(identity?.playerId ? { playerId: identity.playerId } : {}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    // Carry the status so the join-chain retry (#67) can honor the server's retryable
    // transients (409 lease-not-readable / 503 owner-unreachable) during cross-task
    // convergence, while a definitive rejection (403 bad code, identity token) surfaces.
    throw new ApiError(body.error ?? `Join failed (${res.status})`, res.status);
  }
  return (await res.json()) as PlayerTicket;
}
